import React, { useEffect, useRef, useState } from 'react';

/**
 * Bảng chọn hiệu ứng CÓ XEM TRƯỚC cho Cab Studio.
 *
 * Vì sao cần: trước đây "Chuyển cảnh & Màu" là mấy ô dropdown chữ ("Điện ảnh", "Vintage"…),
 * user chọn mà không hình dung được ra sao. Nay mỗi lựa chọn là một ô hình, lấy ĐÚNG khung hình
 * từ clip user vừa tải lên rồi mô phỏng hiệu ứng bằng CSS.
 *
 * Lưu ý: đây là MÔ PHỎNG để hình dung, không phải kết quả render chính xác từng pixel —
 * bản xuất cuối do ffmpeg ở server làm. Mục tiêu là "biết mình đang chọn cái gì".
 */

// Xấp xỉ CSS cho các bộ lọc màu tương ứng bên ffmpeg (MapColorFilter ở media-service).
export const FILTER_CSS: Record<string, string> = {
  '': 'none',
  cinematic: 'contrast(1.14) saturate(1.12)',
  vintage: 'sepia(0.4) saturate(0.85) contrast(1.05)',
  vivid: 'saturate(1.5) contrast(1.08) brightness(1.02)',
  warm: 'sepia(0.18) saturate(1.15) hue-rotate(-10deg)',
  cool: 'saturate(1.1) hue-rotate(12deg) brightness(1.02)',
  bw: 'grayscale(1) contrast(1.08)',
  dreamy: 'saturate(1.1) brightness(1.04) blur(1.2px)',
};

const NOISE_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E\")";

/** Bắt 1 khung hình từ video local (blob url) -> data URL. Không đụng mạng, không lỗi CORS. */
export const grabFrame = (url: string, at: number): Promise<string | null> =>
  new Promise((resolve) => {
    const v = document.createElement('video');
    v.src = url;
    v.muted = true;
    v.crossOrigin = 'anonymous';
    let done = false;
    const finish = (val: string | null) => {
      if (done) return;
      done = true;
      resolve(val);
    };
    v.onerror = () => finish(null);
    v.onloadeddata = () => {
      if (!v.videoWidth) return finish(null);
      v.onseeked = () => {
        try {
          const c = document.createElement('canvas');
          c.width = 160;
          c.height = Math.max(1, Math.round((160 * v.videoHeight) / v.videoWidth));
          const ctx = c.getContext('2d');
          if (!ctx) return finish(null);
          ctx.drawImage(v, 0, 0, c.width, c.height);
          finish(c.toDataURL('image/jpeg', 0.7));
        } catch {
          finish(null);
        }
      };
      v.currentTime = Math.min(Math.max(at, 0.05), Math.max(0.1, v.duration - 0.05));
    };
    setTimeout(() => finish(null), 6000); // không treo mãi nếu video lỗi
  });

/**
 * Lấy 2 khung hình mẫu để minh hoạ.
 * Có ≥2 clip -> lấy cuối clip 1 + đầu clip 2 (đúng y chỗ nối thật sẽ chuyển cảnh).
 * Chỉ 1 clip -> lấy 2 mốc thời gian khác nhau cho dễ thấy sự khác biệt.
 */
export const useSampleFrames = (clips: { url: string; duration: number }[]) => {
  const [frames, setFrames] = useState<{ a: string | null; b: string | null }>({ a: null, b: null });

  // Phụ thuộc vào KHOÁ chuỗi (url + độ dài), KHÔNG phải mảng clips.
  // Nếu phụ thuộc mảng, mỗi lần user kéo cắt (start/end đổi -> mảng mới) sẽ huỷ tiến trình
  // bắt khung hình đang chạy và không thử lại -> ô xem trước trắng vĩnh viễn.
  const key = clips.map((c) => `${c.url}@${Math.round(c.duration * 10)}`).join('|');
  const clipsRef = useRef(clips);
  clipsRef.current = clips;

  useEffect(() => {
    const list = clipsRef.current;
    if (list.length === 0) {
      setFrames({ a: null, b: null });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const c0 = list[0];
      const c1 = list[1];
      const a = await grabFrame(c0.url, c1 ? Math.max(0.1, c0.duration * 0.8) : c0.duration * 0.25);
      if (cancelled) return;
      const b = c1
        ? await grabFrame(c1.url, Math.max(0.1, c1.duration * 0.2))
        : await grabFrame(c0.url, c0.duration * 0.7);
      if (!cancelled) setFrames({ a, b });
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return frames;
};

/** CSS dùng chung (keyframes) — nhúng 1 lần, đặt tiền tố cabfx- để không đụng class khác. */
const FxStyles = () => (
  <style>{`
.cabfx-stage{position:relative;width:100%;height:100%;overflow:hidden;background:#000}
.cabfx-stage img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.cabfx-ph{position:absolute;inset:0;background:linear-gradient(135deg,#0ea5e9,#6366f1)}
.cabfx-ph2{position:absolute;inset:0;background:linear-gradient(135deg,#f59e0b,#ef4444)}

.cabfx-ov{animation-duration:2.8s;animation-iteration-count:infinite;animation-timing-function:ease-in-out}
.cabfx-base-out{animation:cabfx-baseout 2.8s infinite ease-in-out}
@keyframes cabfx-baseout{0%,25%{opacity:1}45%,100%{opacity:0}}

@keyframes cabfx-fade{0%,20%{opacity:0}60%,100%{opacity:1}}
@keyframes cabfx-fadeblack{0%,50%{opacity:0}80%,100%{opacity:1}}
@keyframes cabfx-slideleft{0%,20%{transform:translateX(100%)}65%,100%{transform:translateX(0)}}
@keyframes cabfx-slideright{0%,20%{transform:translateX(-100%)}65%,100%{transform:translateX(0)}}
@keyframes cabfx-wiperight{0%,20%{clip-path:inset(0 100% 0 0)}65%,100%{clip-path:inset(0 0 0 0)}}
@keyframes cabfx-circleopen{0%,20%{clip-path:circle(0% at 50% 50%)}65%,100%{clip-path:circle(80% at 50% 50%)}}
@keyframes cabfx-zoomin{0%,20%{transform:scale(1.6);opacity:0}65%,100%{transform:scale(1);opacity:1}}
@keyframes cabfx-smoothleft{0%,20%{transform:translateX(55%);opacity:0}65%,100%{transform:translateX(0);opacity:1}}

@keyframes cabfx-hardcut{0%,49%{opacity:0}50%,100%{opacity:1}}
.cabfx-hardcut{animation:cabfx-hardcut 2.8s infinite steps(1,end)}
.cabfx-t-fade{animation-name:cabfx-fade}
.cabfx-t-dissolve{animation-name:cabfx-fade}
.cabfx-t-fadeblack{animation-name:cabfx-fadeblack}
.cabfx-t-slideleft{animation-name:cabfx-slideleft}
.cabfx-t-slideright{animation-name:cabfx-slideright}
.cabfx-t-wiperight{animation-name:cabfx-wiperight}
.cabfx-t-circleopen{animation-name:cabfx-circleopen}
.cabfx-t-zoomin{animation-name:cabfx-zoomin}
.cabfx-t-smoothleft{animation-name:cabfx-smoothleft}

@keyframes cabfx-mzoomin{0%{transform:scale(1)}100%{transform:scale(1.28)}}
@keyframes cabfx-mzoomout{0%{transform:scale(1.28)}100%{transform:scale(1)}}
.cabfx-m-zoomin{animation:cabfx-mzoomin 2.6s infinite alternate ease-in-out}
.cabfx-m-zoomout{animation:cabfx-mzoomout 2.6s infinite alternate ease-in-out}

@keyframes cabfx-shake{0%,100%{transform:translate(0,0)}20%{transform:translate(-3px,2px)}40%{transform:translate(3px,-2px)}60%{transform:translate(-2px,-3px)}80%{transform:translate(2px,3px)}}
.cabfx-r-shake{animation:cabfx-shake .45s infinite linear}
@keyframes cabfx-glitch{0%,100%{filter:none;transform:translateX(0)}47%{filter:hue-rotate(0)}50%{filter:hue-rotate(70deg) saturate(1.5);transform:translateX(-3px)}53%{filter:hue-rotate(-50deg);transform:translateX(3px)}56%{filter:none;transform:translateX(0)}}
.cabfx-r-glitch{animation:cabfx-glitch 2.2s infinite steps(1,end)}
.cabfx-r-vignette::after{content:'';position:absolute;inset:0;background:radial-gradient(ellipse at center,rgba(0,0,0,0) 45%,rgba(0,0,0,.75) 100%)}
@keyframes cabfx-grain{0%{background-position:0 0}100%{background-position:120px 120px}}
.cabfx-r-grain::after{content:'';position:absolute;inset:0;opacity:.32;mix-blend-mode:overlay;background-image:${NOISE_BG};animation:cabfx-grain 1.2s infinite steps(4,end)}

@media (prefers-reduced-motion: reduce){
  .cabfx-ov,.cabfx-hardcut,.cabfx-base-out,.cabfx-m-zoomin,.cabfx-m-zoomout,.cabfx-r-shake,.cabfx-r-glitch,.cabfx-r-grain::after{animation:none!important}
  .cabfx-ov{opacity:1}
}
  `}</style>
);

interface TileProps {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const Tile = ({ label, active, onClick, children }: TileProps) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 text-left rounded-lg overflow-hidden border-2 transition ${
      active ? 'border-[#0284c7]' : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
    }`}
    title={label}
  >
    <div className="relative w-[68px] h-[96px] bg-black">{children}</div>
    <div
      className={`text-[11px] px-1 py-1 text-center truncate w-[68px] ${
        active ? 'text-[#0284c7] font-medium' : 'ui-muted'
      }`}
    >
      {label}
    </div>
  </button>
);

/** Ảnh nền cho ô: dùng khung hình thật, chưa có thì dùng ô màu thay thế. */
const Frame = ({
  src,
  alt,
  className,
  second,
  style,
}: {
  src: string | null;
  alt: string;
  className?: string;
  second?: boolean;
  style?: React.CSSProperties;
}) =>
  src ? (
    <img src={src} alt={alt} className={className} style={style} />
  ) : (
    <div className={`${second ? 'cabfx-ph2' : 'cabfx-ph'} ${className ?? ''}`} style={style} />
  );

const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-2 overflow-x-auto pb-1">{children}</div>
);

// ─────────────────────────────── Bộ lọc màu ───────────────────────────────

const FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'Gốc' },
  { value: 'cinematic', label: 'Điện ảnh' },
  { value: 'vintage', label: 'Vintage' },
  { value: 'vivid', label: 'Tươi' },
  { value: 'warm', label: 'Ấm' },
  { value: 'cool', label: 'Lạnh' },
  { value: 'bw', label: 'Đen trắng' },
  { value: 'dreamy', label: 'Mơ màng' },
];

export const FilterPicker = ({
  value,
  onChange,
  frame,
}: {
  value: string;
  onChange: (v: string) => void;
  frame: string | null;
}) => (
  <>
    <FxStyles />
    <Row>
      {FILTERS.map((f) => (
        <Tile key={f.value} label={f.label} active={value === f.value} onClick={() => onChange(f.value)}>
          <div className="cabfx-stage">
            <Frame src={frame} alt={f.label} style={{ filter: FILTER_CSS[f.value] }} />
          </div>
        </Tile>
      ))}
    </Row>
  </>
);

// ─────────────────────────────── Chuyển cảnh ───────────────────────────────

const TRANSITIONS: { value: string; label: string }[] = [
  { value: '', label: 'Cắt cứng' },
  { value: 'fade', label: 'Mờ dần' },
  { value: 'fadeblack', label: 'Qua đen' },
  { value: 'dissolve', label: 'Hoà tan' },
  { value: 'slideleft', label: 'Trượt trái' },
  { value: 'slideright', label: 'Trượt phải' },
  { value: 'wiperight', label: 'Lau ngang' },
  { value: 'circleopen', label: 'Mở vòng' },
  { value: 'zoomin', label: 'Zoom' },
  { value: 'smoothleft', label: 'Mượt' },
];

export const TransitionPicker = ({
  value,
  onChange,
  frameA,
  frameB,
}: {
  value: string;
  onChange: (v: string) => void;
  frameA: string | null;
  frameB: string | null;
}) => (
  <>
    <FxStyles />
    <Row>
      {TRANSITIONS.map((t) => (
        <Tile key={t.value} label={t.label} active={value === t.value} onClick={() => onChange(t.value)}>
          <div className="cabfx-stage">
            <Frame
              src={frameA}
              alt="khung 1"
              className={t.value === 'fadeblack' ? 'cabfx-base-out' : ''}
            />
            <Frame
              src={frameB}
              alt="khung 2"
              second
              className={t.value === '' ? 'cabfx-hardcut' : `cabfx-ov cabfx-t-${t.value}`}
            />
          </div>
        </Tile>
      ))}
    </Row>
  </>
);

// ─────────────────────────────── Zoom động ───────────────────────────────

export const MotionPicker = ({
  value,
  onChange,
  frame,
}: {
  value: string;
  onChange: (v: string) => void;
  frame: string | null;
}) => (
  <>
    <FxStyles />
    <Row>
      {[
        { value: '', label: 'Tắt' },
        { value: 'zoomin', label: 'Phóng vào' },
        { value: 'zoomout', label: 'Lùi ra' },
      ].map((m) => (
        <Tile key={m.value} label={m.label} active={value === m.value} onClick={() => onChange(m.value)}>
          <div className="cabfx-stage">
            <Frame src={frame} alt={m.label} className={m.value ? `cabfx-m-${m.value}` : ''} />
          </div>
        </Tile>
      ))}
    </Row>
  </>
);

// ─────────────────────────────── Hiệu ứng trend ───────────────────────────────

export const TrendPicker = ({
  value,
  onChange,
  frame,
}: {
  value: string;
  onChange: (v: string) => void;
  frame: string | null;
}) => (
  <>
    <FxStyles />
    <Row>
      {[
        { value: '', label: 'Tắt' },
        { value: 'grain', label: 'Hạt phim' },
        { value: 'vignette', label: 'Tối góc' },
        { value: 'shake', label: 'Rung' },
        { value: 'glitch', label: 'Glitch' },
      ].map((r) => (
        <Tile key={r.value} label={r.label} active={value === r.value} onClick={() => onChange(r.value)}>
          <div className={`cabfx-stage ${r.value ? `cabfx-r-${r.value}` : ''}`}>
            <Frame src={frame} alt={r.label} className="" />
          </div>
        </Tile>
      ))}
    </Row>
  </>
);
